// ─────────────────────────────────────────────────────────────────────────────
// ThemeSelector.tsx — Colour scheme + page layout picker
//
// Rendered inside the FE Pattern dropdown in Navbar, below "Switch Framework".
// Provides:
//  - 25 colour schemes (swatch grid)
//  - 10 page layout styles (each with a default colour scheme it auto-applies)
// Both changes apply on the fly via CSS custom properties and data attributes.
//
// Layout → default scheme mapping:
//   Classic   → (no change)
//   Executive → (no change)
//   Scholarly → collegiate-gold
//   Warzone   → warzone-rust
//   Superhero → superhero-cosmic
//   Sci-Fi    → stark-scifi
//   Fantasy   → cosy-fantasy
//   Empire    → empire-imperial
//   Cartoon   → cartoon-pop
//   Pixel     → pixel-retro
// ─────────────────────────────────────────────────────────────────────────────

import { Palette, LayoutGrid } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { setColourScheme } from '@/store/uiSlice';
import { useLayoutSelect } from '@/hooks/useLayoutSelect';
import { COLOUR_SCHEMES } from '@/styles/colourSchemes';
import { LAYOUTS } from '@/data/navOptions';

// ── Component ──────────────────────────────────────────────────────────────────

interface ThemeSelectorProps {
  /** Whether dark mode is currently resolved — needed for label contrast */
  isDark: boolean;
}

export function ThemeSelector({ isDark: _isDark }: ThemeSelectorProps) {
  const dispatch      = useAppDispatch();
  const activeScheme  = useAppSelector((s) => s.ui.colourScheme);
  const activeLayout  = useAppSelector((s) => s.ui.cvLayout);
  const selectLayout  = useLayoutSelect();

  const activeSchemeObj = COLOUR_SCHEMES.find((s) => s.id === activeScheme);

  return (
    <>
      {/* ── Colour Schemes ─────────────────────────────────────────────── */}
      <div className="border-t border-slate-200 dark:border-navy-600 p-3">
        <p className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider px-1 mb-2.5 flex items-center gap-1.5">
          <Palette size={11} />
          Colour Scheme
        </p>

        {/* Swatch grid — 5 columns for 25 schemes */}
        <div className="grid grid-cols-5 gap-1.5 px-1">
          {COLOUR_SCHEMES.map((scheme) => (
            <button
              key={scheme.id}
              onClick={() => dispatch(setColourScheme(scheme.id))}
              title={scheme.name}
              aria-label={scheme.name}
              aria-pressed={activeScheme === scheme.id}
              className={`
                w-7 h-7 rounded-full border-2 transition-all duration-150
                hover:scale-110 hover:shadow-md
                ${activeScheme === scheme.id
                  ? 'border-white shadow-lg scale-110 ring-2 ring-offset-1 ring-slate-300 dark:ring-navy-400'
                  : 'border-transparent opacity-75 hover:opacity-100'
                }
              `}
              style={{
                background: `linear-gradient(135deg, ${scheme.hero} 50%, ${scheme.accent} 50%)`,
              }}
            />
          ))}
        </div>

        {/* Active scheme name */}
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-2 px-1 font-medium">
          {activeSchemeObj?.name ?? ''}
        </p>
      </div>

      {/* ── Page Layout ────────────────────────────────────────────────── */}
      <div className="border-t border-slate-200 dark:border-navy-600 p-2">
        <p className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider px-3 py-1.5 flex items-center gap-1.5">
          <LayoutGrid size={11} />
          Page Layout
        </p>

        {LAYOUTS.map((layout) => (
          <button
            key={layout.value}
            onClick={() => selectLayout(layout)}
            className={`
              w-full text-left px-3 py-2 rounded-lg transition-colors flex items-start gap-2.5
              ${activeLayout === layout.value
                ? 'bg-accent-soft text-accent'
                : 'hover:bg-slate-50 dark:hover:bg-navy-700 text-slate-700 dark:text-slate-300'
              }
            `}
            role="option"
            aria-selected={activeLayout === layout.value}
          >
            <span className="mt-0.5 text-sm w-5 text-center font-mono shrink-0">
              {layout.icon}
            </span>
            <span>
              <span className="block text-sm font-medium leading-tight">{layout.label}</span>
              <span className="block text-xs text-slate-400 dark:text-slate-500 mt-0.5">{layout.desc}</span>
            </span>
          </button>
        ))}
      </div>
    </>
  );
}
